import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../hooks/auth/useAuth';
import useDialogContext from '../../../hooks/dialog/useDialogContext';
import { PCFBiogenicEmissionsMode } from '../../../shared/enums/pcf';
import { IComponent } from '../../../shared/interfaces/IComponent';

type ExportOrRequestPcfsButtonsProps = {
  biogenicEmissionsMode: PCFBiogenicEmissionsMode;
  componentsToShowPendingDuringBulk: IComponent[];
  setComponentsToShowPendingDuringBulk: (products: IComponent[]) => void;
};

const ExportOrRequestPcfsButtons = ({
  biogenicEmissionsMode,
  componentsToShowPendingDuringBulk,
  setComponentsToShowPendingDuringBulk,
}: ExportOrRequestPcfsButtonsProps): JSX.Element | null => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { openDialog } = useDialogContext();
  const buttonLabel = t('componentsPage.bulkActions');

  const menuRef = useRef<Menu>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [syncedWidth, setSyncedWidth] = useState<number>();

  useEffect(() => {
    // Reset width on label change (e.g language switch) to allow natural resizing.
    setSyncedWidth(undefined);
  }, [buttonLabel]);

  if (user?.company?.license === 'basic') {
    return null;
  }

  useEffect(() => {
    const handleResize = () => {
      // Reset synced width on resize to allow components to resize naturally
      setSyncedWidth(undefined);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      return window.removeEventListener('resize', handleResize);
    };
  }, []);

  /**
   * Calculates a single width that will be shared by both
   * the Button and the popup Menu
   */
  const syncWidths = () => {
    const buttonWidth = buttonRef.current?.offsetWidth ?? 0;
    requestAnimationFrame(() => {
      const menuRoot = menuRef.current?.getElement();
      const menuWidth = menuRoot?.getBoundingClientRect().width ?? 0;

      setSyncedWidth(Math.max(buttonWidth, menuWidth));
    });
  };

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    menuRef?.current?.toggle(event);
  };

  const menuItems: MenuItem[] = [
    {
      id: 'export-pcfs-dialog',
      label: t('componentsPage.pcfExport'),
      command: () => {
        return openDialog('export-pcfs-dialog', { biogenicEmissionsMode });
      },
    },
    {
      id: 'bulk-request-pcfs-dialog',
      label: t('componentsPage.pcfRequest'),
      command: () => {
        return openDialog('bulk-request-pcfs-dialog', {
          componentsToShowPendingDuringBulk,
          setComponentsToShowPendingDuringBulk,
        });
      },
    },
  ];

  if (user?.company?.license === 'reseller') {
    menuItems.unshift({
      id: 'request-bulk-transfer-components-dialog',
      label: t('componentsPage.transferComponent'),
      command: () => {
        return openDialog('request-bulk-transfer-components-dialog');
      },
    });
  }

  return (
    <>
      <Menu
        ref={menuRef}
        popup
        model={menuItems}
        onShow={syncWidths}
        style={{ width: syncedWidth ? `${syncedWidth}px` : 'auto' }}
      />

      <Button
        label={buttonLabel}
        onClick={onClick}
        style={{ width: syncedWidth ? `${syncedWidth}px` : 'auto' }}
        pt={{
          root: {
            ref: buttonRef,
          },
        }}
      />
    </>
  );
};

export default ExportOrRequestPcfsButtons;
